import { createFormContext, zodResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import { useRouter } from 'next/router'
import { useEffect, type HTMLAttributes } from 'react'

import { useMutationCreateInquiry } from '@flecto/core/store'
import { useNotifications } from '@flecto/ui'
import { useHandleAxiosError } from '@flecto/utils'

import { useStoreBasket } from '../../hooks/useStoreBasket'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStoreLocalStorage } from '../../hooks/useStoreLocalStorage'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'
import { type OrderForm } from '../../types/OrderForm'
import { useCheckoutSchema } from './useCheckoutSchema'
import { useFormCheckoutState } from './useFormCheckoutState'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<OrderForm>()

type FormCheckoutProps = HTMLAttributes<HTMLFormElement>

export function FormCheckout({ children, ...props }: FormCheckoutProps) {
  const router = useRouter()
  const path = useStorePathBuilder()
  const store = useStoreDetails()

  const { getErrorMessages } = useHandleAxiosError()
  const { products, clearBasket } = useStoreBasket()
  const { setIsLoading } = useFormCheckoutState()
  const { schema, initialValues } = useCheckoutSchema()
  const { mutateAsync: createInquiry } = useMutationCreateInquiry()
  const { showNotification } = useNotifications()
  const { setOrderConfirmed } = useStoreLocalStorage()

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  })

  useEffect(() => {
    console.error('ERRORS: ', form.errors)
  }, [form.errors])

  const handleConfirmCheckout = async () => {
    try {
      setIsLoading(true)

      form.values.phone_number = `${form.values._phone_number_country_code}${form.values._phone_number}`

      form.values.inquiry_items_attributes = products.map((i) => ({
        item_id: i.product.id,
        quantity: i.quantity,
      }))

      const { inquiry } = await createInquiry({
        storeDomain: store?.domain,
        inquiry: form.values,
      })

      setOrderConfirmed(inquiry)
      await router.push(path('/confirmation'))
      clearBasket()
    } catch (e) {
      if (e instanceof AxiosError) {
        const errors = getErrorMessages(e, {
          'inquiry[phone_number]': '_phone_number',
        })

        errors.forEach((e) => {
          form.setFieldError(e.field, e.errorMessage)
        })
      }
      showNotification({
        type: 'error',
        message: 'Error finishing checkout. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider form={form}>
      <form
        {...props}
        onSubmit={form.onSubmit(
          () => handleConfirmCheckout(),
          () => window.scrollTo({ top: 0, behavior: 'smooth' })
        )}
      >
        {children}
      </form>
    </FormProvider>
  )
}
